
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, Mail, Shield, HelpCircle } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  return (
    <footer className="bg-slate-900 text-slate-100 border-t border-slate-700">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Company Info */}
          <div className="md:col-span-2 text-center md:text-left">
            <p className="text-slate-200 mb-4 max-w-md mx-auto md:mx-0">
              Consultoria de IA por assinatura que simplifica processos e impulsiona resultados com soluções acessíveis e inteligentes.
            </p>
            <div className="flex gap-4 justify-center md:justify-start">
              <a 
                href="https://instagram.com/ai4me.com.br" 
                target="_blank" 
                rel="noopener noreferrer"
                title="Instagram AI4ME"
                className="text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://youtube.com/@ai4me" 
                target="_blank" 
                rel="noopener noreferrer"
                title="YouTube AI4ME"
                className="text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a 
                href="https://linkedin.com/company/ai4me" 
                target="_blank" 
                rel="noopener noreferrer"
                title="LinkedIn AI4ME"
                className="text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Suporte */}
          <div className="flex flex-col items-start w-full">
            <h2 className="font-semibold mb-4 text-white text-lg">Suporte</h2>
            <ul className="space-y-3 md:space-y-2 w-full">
              <li className="flex justify-start">
                <a 
                  href="https://wa.me/5561985006969?text=Olá! Preciso de ajuda com o AI4ME." 
                  target="_blank" 
                  rel="noopener noreferrer"
                  title="WhatsApp AI4ME"
                  className="text-slate-300 hover:text-white transition-colors inline-flex items-center gap-2 touch-manipulation"
                >
                  <MessageCircle className="w-4 h-4 flex-shrink-0" />
                  <span>WhatsApp</span>
                </a>
              </li>
              <li className="flex justify-start">
                <button 
                  onClick={() => handleNavigate('/ajuda')}
                  title="Central de ajuda e FAQ"
                  className="text-slate-300 hover:text-white transition-colors inline-flex items-center gap-2 touch-manipulation"
                >
                  <HelpCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Central de Ajuda</span>
                </button>
              </li>
              <li className="flex justify-start">
                <a 
                  href="mailto:equipe@ai4mebrasil.com"
                  title="E-mail de contato"
                  className="text-slate-300 hover:text-white transition-colors inline-flex items-center gap-2 touch-manipulation"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span>E-mail</span>
                </a>
              </li>
              <li className="flex justify-start">
                <button 
                  onClick={() => handleNavigate('/lgpd')}
                  title="Política de privacidade LGPD"
                  className="text-slate-300 hover:text-white transition-colors inline-flex items-center gap-2 touch-manipulation"
                >
                  <Shield className="w-4 h-4 flex-shrink-0" />
                  <span>Política de Proteção de Dados (LGPD)</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-slate-700 mt-8 pt-8 text-center">
          <p className="text-slate-300 text-sm">
            © {currentYear} AI4ME - Serviço de Inteligência Artificial por Assinatura. 
            Todos os direitos reservados. | 
            <button 
              onClick={() => handleNavigate('/lgpd')}
              title="Política LGPD"
              className="hover:text-white transition-colors ml-1"
            >
              Política de Privacidade
            </button>
          </p>
          <p className="text-slate-300 text-sm mt-2">
            Brasília/DF
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;